import type {FilledContentRelationshipField} from '@prismicio/client'
import {SliceZone} from '@prismicio/react'
import Head from 'next/head'
import type {
  ButtonDocument,
  GlossaryPageDocumentData,
  GlossaryTermDocument,
  NavigationDocumentData,
  PickContentRelationshipFieldData,
  Simplify,
} from 'prismicio-types'
import {useMemo} from 'react'
import {components} from 'slices'
import {getButtonsData} from 'src/common/helpers'
import {Layout} from 'src/common/Layout'
import {Meta} from 'src/common/Meta'
import {getMetaProps} from 'src/common/utils/documentHelpers'
import {TermContent} from './TermContent'
import {generateGlossaryStructuredData, sanitizeJsonLd} from './utils'

export interface GlossaryTermProps {
  term: GlossaryTermDocument
  parentPage: FilledContentRelationshipField<
    'glossary_page',
    string,
    PickContentRelationshipFieldData<
      {
        id: 'glossary_page'
        fields: [
          {
            id: 'breadcrumbs'
            fields: ['label', 'link']
          },
          'title',
          'related_terms_heading',
        ]
      },
      Simplify<GlossaryPageDocumentData>,
      string
    >
  >
  relatedTerms: Array<GlossaryTermDocument>
  navigationData: NavigationDocumentData
  buttonsData?: Array<ButtonDocument>
}

export function GlossaryTerm(props: GlossaryTermProps) {
  const {term, parentPage, relatedTerms, navigationData, buttonsData} = props

  const {primaryButtonData: primaryNavButton, secondaryButtonData: secondaryNavButton} = getButtonsData({
    buttonsData,
    primaryButton: term.data.primary_nav_button,
    secondaryButton: term.data.secondary_nav_button,
  })

  const structuredData = useMemo(
    () =>
      generateGlossaryStructuredData({
        term: term.data.term,
        imageUrl: term.data.meta_image?.url || '',
        author: term.data.author,
        publishedDate: term.data.published_date ?? term.first_publication_date,
        lastUpdatedDate: term.data.last_updated_date ?? term.last_publication_date,
      }),
    [
      term.data.term,
      term.data.meta_image?.url,
      term.data.author,
      term.data.published_date,
      term.data.last_updated_date,
      term.first_publication_date,
      term.last_publication_date,
    ],
  )

  return (
    <Layout
      alternateLanguages={term.alternate_languages}
      navigationData={navigationData}
      pageUid={term.uid}
      locale={term.lang}
      primaryNavButton={primaryNavButton}
      secondaryNavButton={secondaryNavButton}
      hidePrimaryNavButton={!!term.data.hide_primary_nav_button}
      hideSecondaryNavButton={!!term.data.hide_secondary_nav_button}
      primaryNavButtonScrollText={term.data.primary_nav_button_scroll_into_view_text}
      primaryNavButtonScrollId={term.data.primary_nav_button_scroll_into_view_id}
      secondaryNavButtonScrollText={term.data.secondary_nav_button_scroll_into_view_text}
      secondaryNavButtonScrollId={term.data.secondary_nav_button_scroll_into_view_id}
    >
      <Meta {...getMetaProps(term)} />
      {structuredData && (
        <Head>
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{__html: sanitizeJsonLd(structuredData)}}
            key="glossary-structured-data"
          />
        </Head>
      )}

      <TermContent term={term} parentPage={parentPage} relatedTerms={relatedTerms} />
      {term.data.slices && term.data.slices.length > 0 && (
        <SliceZone components={components} slices={term.data.slices} context={{pageUid: term.uid}} />
      )}
    </Layout>
  )
}
